/**
 * Marketplace + HitPay payment API client.
 * Money is integer cents. Currency is always SGD.
 */
import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';

/**
 * A purchasable option of a product. Each variant owns its price and maps to one HitPay
 * product (external_id, dashboard parity — checkout still uses the dynamic payment-request
 * API keyed by variant id). linked_studios drives the studio → products reverse lookup.
 */
export interface MarketVariant {
  id: string;
  product_id: string;
  name: string;
  external_id: string;
  price_cents: number;
  original_price_cents: number; // 0 = none
  currency: string;
  metadata: Record<string, unknown>; // type-specific, e.g. { classesPerStudio: 2 }
  sort_order: number;
  is_active: boolean;
  linked_studios: string[]; // studio ids
  studios: LinkedStudio[];
}

interface LinkedStudio {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  averageRating: number;
  reviewCount: number;
}

/**
 * A typed marketplace product. `type` discriminates the shape ("csp", ...); `content`
 * carries the type-specific presentation payload; `variants` are the purchasable options.
 */
export interface MarketProduct {
  id: string; // stable UUID
  type: string; // discriminator: "csp", ...
  slug: string;
  title: string;
  description: string;
  price_cents: number; // headline price (cheapest variant / product default)
  original_price_cents: number; // 0 = none
  currency: string;
  content: Record<string, unknown>; // type-specific; cast per `type`
  is_active: boolean;
  sort_order: number;
  variants: MarketVariant[];
}

export type PaymentStatus =
  'awaiting_payment' | 'paid' | 'payment_failed' | 'payment_expired' | 'refunded';

type FulfillmentStatus =
  'sent_to_studio' | 'studio_processing' | 'class_credited' | 'fulfillment_failed';

export type AttemptStatus = 'pending' | 'completed' | 'failed' | 'expired' | 'cancelled';

export interface Purchase {
  id: string;
  variant_id: string;
  amount_cents: number;
  currency: string;
  payment_status: PaymentStatus;
  fulfillment_status: FulfillmentStatus | null;
  fulfillment_updated_at: string | null;
  fulfilled_at: string | null;
  purchaser_name: string;
  purchaser_email: string;
  purchaser_phone: string;
  expires_at: string | null;
  created_at: string;
  // Display-only, populated by enriched list/detail reads.
  product_id?: string;
  product_title?: string;
  variant_name?: string;
}

export interface PurchaseDetail extends Purchase {
  attempts: PaymentAttempt[];
}

interface PaymentAttempt {
  id: string;
  status: AttemptStatus;
  reference_number: string;
  amount_cents: number;
  payment_method: string;
  paid_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CheckoutResponse {
  purchase_id: string;
  attempt_id: string;
  checkout_url: string;
  expires_at: string;
}

export interface PurchaseStatusResponse {
  payment_status: PaymentStatus;
  fulfillment_status: FulfillmentStatus | null;
}

/** Cursor pagination returned by GET /v1/market/purchases (server field is
 * `hasNext`, matching the shared PaginationMeta, not `hasMore`). */
interface PurchasePagination {
  limit: number;
  cursor: string;
  hasNext: boolean;
}

/** Envelope for GET /v1/market/purchases. Unlike community/favorites (which nest
 * pagination inside `data`), this endpoint puts `pagination` beside `data`, so
 * the envelope must be read raw to follow the cursor. */
export interface PurchaseListEnvelope {
  data: Purchase[];
  success: boolean;
  timestamp: string;
  request_id: string;
  pagination: PurchasePagination;
}

export const marketApi = {
  listProducts: () =>
    apiClient.get<MarketProduct[]>('/v1/market/products') as Promise<
      Result<MarketProduct[], ApiError>
    >,

  getProduct: (id: string) =>
    apiClient.get<MarketProduct>(`/v1/market/products/${id}`) as Promise<
      Result<MarketProduct, ApiError>
    >,

  // Products sold at a given studio (reverse lookup via variant → linked studios).
  listStudioProducts: (studioId: string) =>
    apiClient.get<MarketProduct[]>(`/v1/market/studios/${studioId}/products`) as Promise<
      Result<MarketProduct[], ApiError>
    >,

  checkout: (input: {
    variant_id: string;
    idempotency_key: string;
    name?: string;
    email?: string;
    phone?: string;
  }) =>
    apiClient.post<CheckoutResponse>('/v1/market/checkout', input) as Promise<
      Result<CheckoutResponse, ApiError>
    >,

  retry: (purchaseId: string) =>
    apiClient.post<CheckoutResponse>(`/v1/market/purchases/${purchaseId}/retry`, {}) as Promise<
      Result<CheckoutResponse, ApiError>
    >,

  getPurchase: (id: string) =>
    apiClient.get<PurchaseDetail>(`/v1/market/purchases/${id}`) as Promise<
      Result<PurchaseDetail, ApiError>
    >,

  getStatus: (id: string) =>
    apiClient.get<PurchaseStatusResponse>(`/v1/market/purchases/${id}/status`) as Promise<
      Result<PurchaseStatusResponse, ApiError>
    >,

  listPurchases: async (): Promise<Result<Purchase[], ApiError>> => {
    // The server caps each response (max 100); request the max page size so a
    // 101-purchase history takes two calls instead of six. Walk the cursor here
    // so the profile page assembles the full history and its client-side
    // filter/sort/paging keeps working unchanged. Mirrors fetchPurchasesServer
    // so a background refetch produces the same set.
    const all: Purchase[] = [];
    let cursor: string | null = null;
    // ponytail: defensive cap — a misbehaving cursor (hasNext never false)
    // would otherwise spin forever on the profile page. 100 pages × 100 = 10000.
    for (let page = 0; page < 100; page++) {
      const endpoint: string = cursor
        ? `/v1/market/purchases?cursor=${encodeURIComponent(cursor)}&limit=100`
        : '/v1/market/purchases?limit=100';
      const res: Result<PurchaseListEnvelope, ApiError> =
        await apiClient.getRaw<PurchaseListEnvelope>(endpoint);
      if (!res.ok) return err(res.error);
      all.push(...res.value.data);
      if (!res.value.pagination?.hasNext || !res.value.pagination?.cursor) break;
      cursor = res.value.pagination.cursor;
    }
    return ok(all);
  },
};
